import { DateHelper } from '@helpers/date.helper';
import { AccountingPeriod, AccountingPeriodJson } from '@models/accounting-period';
import { Company, CompanyJson } from '@models/company';
import { Contributor, ContributorJson } from '@models/contributor';

import { FormalitySpecificDates, FormalitySpecificDatesJson } from './formality-specific-dates';
import { LegalFormalityStatus, LegalFormalityStatusJson } from './legal-formality-status';
import { FormalitySupervisorEnum } from '../enums/formality-supervisor.enum';

export interface FormalityBaseJson {
  id?: number;
  formality_type?: string;
  code_name?: string;
  status?: string;
  formality_supervisor?: FormalitySupervisorEnum;
  delivery_date?: string;
  statuses?: LegalFormalityStatusJson[];
  business_unit_id?: number;
  production?: Partial<ContributorJson>;
  note?: string;
  accounting_period?: Partial<AccountingPeriodJson>;
  company?: Partial<CompanyJson>;
  specific_dates?: FormalitySpecificDatesJson;
}

export class FormalityBase {
  constructor(
    public id?: number,
    public formalityType?: string,
    public codeName?: string,
    public status?: string,
    public formalitySupervisor?: FormalitySupervisorEnum,
    public deliveryDate?: Date,
    public statuses?: LegalFormalityStatus[],
    public businessUnitId?: number,
    public production?: Contributor,
    public note?: string,
    public accountingPeriod?: AccountingPeriod,
    public company?: Company,
    public specificDates?: FormalitySpecificDates
  ) {}

  static toCreateFormalityJson(formality: FormalityBase): Partial<FormalityBaseJson> {
    return {
      formality_type: formality.formalityType,
      code_name: formality.codeName,
      accounting_period: formality.accountingPeriod
    };
  }

  static toCreateCapitalIncomeDeclarationFormalityJson(formality: FormalityBase): Partial<FormalityBaseJson> {
    return {
      formality_type: formality.formalityType,
      code_name: formality.codeName,
      specific_dates: formality.specificDates ? FormalitySpecificDates.toJson(formality.specificDates) : null
    };
  }

  static toDeliveryDateFormalityJson(formality: Partial<FormalityBase>): Partial<FormalityBaseJson> {
    return {
      id: formality.id,
      delivery_date: formality.deliveryDate ? DateHelper.format(formality.deliveryDate, 'YYYY-MM-DD') : null
    };
  }

  static toDeliveryDateFormalityCreationJson(formality: Partial<FormalityBase>): Partial<FormalityBaseJson> {
    return {
      delivery_date: formality.deliveryDate ? DateHelper.format(formality.deliveryDate, 'YYYY-MM-DD') : null
    };
  }

  static toProductionFormalityJson(formality: Partial<FormalityBase>): Partial<FormalityBaseJson> {
    return {
      id: formality.id,
      production: formality.production ? Contributor.toJson(formality.production) : null
    };
  }

  static toNoteFormalityJson(formality: Partial<FormalityBase>): Partial<FormalityBaseJson> {
    return {
      id: formality.id,
      note: formality.note
    };
  }

  static fromJson(json: FormalityBaseJson): FormalityBase {
    return new FormalityBase(
      json.id,
      json.formality_type,
      json.code_name,
      json.status,
      json.formality_supervisor,
      json.delivery_date ? DateHelper.fromUtc(json.delivery_date) : null,
      json.statuses
        ? json.statuses.map((legalFormalityStatusJson: LegalFormalityStatusJson) =>
            LegalFormalityStatus.fromJson(legalFormalityStatusJson)
          )
        : null,
      json.business_unit_id,
      json.production ? Contributor.fromJson(json.production) : null,
      json.note,
      json.accounting_period ? AccountingPeriod.fromJson(json.accounting_period) : null,
      json.company ? Company.fromJson(json.company) : null,
      json.specific_dates ? FormalitySpecificDates.fromJson(json.specific_dates) : null
    );
  }

  static toJson(formality: FormalityBase): FormalityBaseJson {
    return {
      id: formality.id,
      formality_type: formality.formalityType,
      code_name: formality.codeName,
      status: formality.status,
      formality_supervisor: formality.formalitySupervisor,
      delivery_date: formality.deliveryDate ? DateHelper.format(formality.deliveryDate, 'YYYY-MM-DD') : null,
      statuses: formality.statuses
        ? formality.statuses.map((legalFormalityStatus: LegalFormalityStatus) =>
            LegalFormalityStatus.toJson(legalFormalityStatus)
          )
        : [],
      business_unit_id: formality.businessUnitId,
      production: formality.production ? Contributor.toJson(formality.production) : null,
      note: formality.note,
      accounting_period: formality.accountingPeriod ? AccountingPeriod.toJson(formality.accountingPeriod) : null,
      company: formality.company ? Company.toJson(formality.company) : null,
      specific_dates: formality.specificDates ? FormalitySpecificDates.toJson(formality.specificDates) : null
    };
  }
}
